import { useEffect, useState } from "react";
import {
  Plus,
  PencilSimple,
  Trash,
  CircleNotch,
  X,
  Eye,
  EyeSlash,
  Star,
} from "@phosphor-icons/react";
import {
  listAwards,
  createAward,
  updateAward,
  deleteAward,
} from "@/lib/api";
import FileUploadField from "./FileUploadField";

const EMPTY = {
  year: new Date().getFullYear().toString(),
  product: "",
  brand: "",
  category: "",
  image_url: null,
  featured: false,
  sort_order: 0,
  is_published: true,
};

export default function AwardsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    setError("");
    listAwards({ all: true })
      .then((r) => setItems(r.items || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (payload) => {
    setBusy(true);
    setError("");
    try {
      if (payload.id) {
        await updateAward(payload.id, payload);
      } else {
        await createAward(payload);
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteAward(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Premi & Awards</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tighter font-medium">
            {items.length}{" "}
            <span className="text-[#5C5A55] font-light">
              {items.length === 1 ? "premio" : "premi"}
            </span>
          </h2>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="awards-add-btn"
          className="btn-primary"
        >
          <Plus size={16} weight="bold" />
          Nuovo premio
        </button>
      </div>

      {error && (
        <div className="p-3 bg-[#EB0D0D]/8 text-[#C00A0A] text-sm border border-[#EB0D0D]/30">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <CircleNotch
            size={28}
            weight="bold"
            className="animate-spin text-[#EB0D0D]"
          />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[#F0EFEB] border border-[#D1CFC7]/40 py-16 text-center text-[#5C5A55]">
          Nessun premio ancora. Clicca "Nuovo premio" per iniziare.
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-awards-list">
          {items.map((a) => (
            <div
              key={a.id}
              className={`bg-[#F0EFEB] p-5 border border-[#D1CFC7]/40 grid grid-cols-1 md:grid-cols-12 gap-4 items-center ${
                !a.is_published ? "opacity-60" : ""
              }`}
              data-testid={`award-row-${a.id}`}
            >
              <div className="md:col-span-1">
                {a.image_url ? (
                  <img
                    src={a.image_url}
                    alt=""
                    className="w-16 h-16 object-cover border border-[#D1CFC7]"
                  />
                ) : (
                  <div className="w-16 h-16 bg-[#E2E0D8] border border-[#D1CFC7]" />
                )}
              </div>
              <div className="md:col-span-5 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-bold bg-[#EB0D0D] text-[#FAF9F6] px-2 py-0.5">
                    Vincitore {a.year}
                  </span>
                  {a.featured && (
                    <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#EB0D0D] inline-flex items-center gap-1">
                      <Star size={12} weight="fill" />
                      In evidenza
                    </span>
                  )}
                  {!a.is_published && (
                    <span className="text-[10px] uppercase tracking-[0.15em] font-bold bg-[#5C5A55] text-[#FAF9F6] px-2 py-0.5">
                      Nascosto
                    </span>
                  )}
                </div>
                <div className="font-display text-lg truncate">{a.product}</div>
                {a.brand && (
                  <div className="text-xs text-[#5C5A55] truncate">
                    {a.brand}
                  </div>
                )}
              </div>
              <div className="md:col-span-4 text-sm text-[#5C5A55] truncate">
                {a.category || <span className="italic">Nessuna categoria</span>}
              </div>
              <div className="md:col-span-2 flex md:justify-end gap-1">
                <button
                  onClick={() => setEditing({ ...a })}
                  className="p-2 hover:bg-[#FAF9F6] text-[#1A1918] transition-colors"
                  title="Modifica"
                  data-testid={`edit-award-${a.id}`}
                >
                  <PencilSimple size={16} weight="regular" />
                </button>
                <button
                  onClick={() => setDeleteTarget(a)}
                  className="p-2 hover:bg-[#EB0D0D]/10 text-[#EB0D0D] transition-colors"
                  title="Elimina"
                  data-testid={`delete-award-${a.id}`}
                >
                  <Trash size={16} weight="regular" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <AwardEditModal
          initial={editing}
          onSave={save}
          onCancel={() => setEditing(null)}
          busy={busy}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          title={deleteTarget.product}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          busy={busy}
        />
      )}
    </div>
  );
}

function AwardEditModal({ initial, onSave, onCancel, busy }) {
  const [form, setForm] = useState(initial);
  const isNew = !initial.id;
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.year.trim() || !form.product.trim()) return;
    onSave(form);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1A1918]/60 backdrop-blur-sm"
      onClick={busy ? null : onCancel}
    >
      <form
        onSubmit={onSubmit}
        className="bg-[#FAF9F6] w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#D1CFC7] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="award-edit-modal"
      >
        <div className="flex items-start justify-between p-6 md:p-8 border-b border-[#D1CFC7]/60 sticky top-0 bg-[#FAF9F6] z-10">
          <div>
            <div className="overline mb-1">
              {isNew ? "Nuovo premio" : "Modifica premio"}
            </div>
            <h3 className="font-display text-2xl tracking-tighter font-medium">
              {form.product || "Senza nome"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="p-2 hover:bg-[#F0EFEB] transition-colors disabled:opacity-50"
            aria-label="Chiudi"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Anno *">
              <input
                type="text"
                required
                value={form.year}
                onChange={(e) => update({ year: e.target.value })}
                placeholder="2025"
                className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D]"
                data-testid="award-year-input"
              />
            </Field>
            <div className="md:col-span-2">
              <Field label="Prodotto *">
                <input
                  type="text"
                  required
                  value={form.product}
                  onChange={(e) => update({ product: e.target.value })}
                  placeholder="Es. New Termic Linear"
                  className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D]"
                  data-testid="award-product-input"
                />
              </Field>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Brand">
              <input
                type="text"
                value={form.brand || ""}
                onChange={(e) => update({ brand: e.target.value })}
                placeholder="Es. Korus"
                className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D]"
              />
            </Field>
            <Field label="Categoria">
              <input
                type="text"
                value={form.category || ""}
                onChange={(e) => update({ category: e.target.value })}
                placeholder="Es. Finestre"
                className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D]"
              />
            </Field>
          </div>

          <FileUploadField
            kind="image"
            label="Immagine del premio"
            value={form.image_url}
            onChange={(url) => update({ image_url: url })}
            hint="JPG / PNG / WebP · max 5MB"
          />

          <Field label="Ordine di visualizzazione">
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) =>
                update({ sort_order: parseInt(e.target.value, 10) || 0 })
              }
              className="w-full max-w-[180px] bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D]"
            />
          </Field>

          <div className="space-y-3 border-t border-[#D1CFC7]/60 pt-5">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.featured}
                onChange={(e) => update({ featured: e.target.checked })}
                className="w-4 h-4 accent-[#EB0D0D]"
              />
              <span className="inline-flex items-center gap-2 text-sm text-[#1A1918]">
                <Star
                  size={16}
                  weight={form.featured ? "fill" : "regular"}
                  className="text-[#EB0D0D]"
                />
                In evidenza (mostrato in primo piano)
              </span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.is_published}
                onChange={(e) => update({ is_published: e.target.checked })}
                className="w-4 h-4 accent-[#EB0D0D]"
              />
              <span className="inline-flex items-center gap-2 text-sm text-[#1A1918]">
                {form.is_published ? (
                  <Eye size={16} weight="bold" className="text-[#EB0D0D]" />
                ) : (
                  <EyeSlash size={16} weight="bold" className="text-[#5C5A55]" />
                )}
                Pubblicato sul sito
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-6 md:p-8 border-t border-[#D1CFC7]/60 bg-[#F0EFEB] sticky bottom-0">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-transparent text-[#1A1918] border border-[#D1CFC7] hover:border-[#1A1918] transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={busy || !form.year.trim() || !form.product.trim()}
            data-testid="award-save-btn"
            className="btn-primary disabled:opacity-50"
          >
            {busy ? (
              <>
                <CircleNotch size={14} weight="bold" className="animate-spin" />
                Salvataggio…
              </>
            ) : (
              <>{isNew ? "Crea premio" : "Salva modifiche"}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function DeleteConfirmModal({ title, onConfirm, onCancel, busy }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#1A1918]/60 backdrop-blur-sm"
      onClick={busy ? null : onCancel}
    >
      <div
        className="bg-[#FAF9F6] w-full max-w-md border border-[#D1CFC7] shadow-2xl p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="overline mb-3">Conferma eliminazione</div>
        <h3 className="font-display text-2xl tracking-tighter font-medium mb-3">
          Eliminare{" "}
          <em className="italic font-light text-[#EB0D0D]">{title}</em>?
        </h3>
        <p className="text-sm text-[#5C5A55] mb-6">
          Il premio verrà rimosso dal sito. L'immagine caricata resta nel
          server.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-transparent text-[#1A1918] border border-[#D1CFC7] hover:border-[#1A1918] transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="award-delete-confirm"
            className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-[#EB0D0D] text-[#FAF9F6] hover:bg-[#C00A0A] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy ? (
              <CircleNotch size={14} weight="bold" className="animate-spin" />
            ) : (
              <Trash size={14} weight="bold" />
            )}
            Elimina
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] font-bold mb-2 text-[#1A1918]">
        {label}
      </label>
      {children}
    </div>
  );
}
